/**
 * The terminal is redesignable at three levels, from lightest to heaviest:
 *
 *   1. `tokens`     — CSS custom properties (colors, radius, font). Cheapest.
 *   2. `classNames` — per-slot class overrides that REPLACE the Tailwind
 *                     defaults. Reach for this when you want a different layout
 *                     engine or design system.
 *   3. `slots`      — full render-prop replacement of a region (see TerminalProps).
 *
 * Everything below is a token or a slot class key.
 */

export interface TerminalThemeTokens {
  /** Panel + status-bar background. */
  bg: string;
  /** Slightly raised surfaces (input, assistant bubble). */
  surface: string;
  /** Primary foreground text. */
  fg: string;
  /** Muted/secondary text. */
  fgMuted: string;
  /** Accent used for the caret, active states, primary buttons. */
  accent: string;
  /** Foreground on top of the accent color. */
  accentFg: string;
  /** Hairline borders. */
  border: string;
  /** Destructive / error color. */
  danger: string;
  /** Monospace font stack for the terminal body. */
  fontMono: string;
  /** Base corner radius. */
  radius: string;
}

/**
 * Every visual slot the terminal renders. Provide any subset to override the
 * built-in Tailwind classes for that slot. An override REPLACES the default.
 */
export interface TerminalClassNames {
  root: string;
  statusBar: string;
  statusBarButton: string;
  statusBarLabel: string;
  panel: string;
  panelHeader: string;
  messageList: string;
  message: string;
  messageUser: string;
  messageAssistant: string;
  messageSystem: string;
  messageError: string;
  inputBar: string;
  input: string;
  sendButton: string;
  resizeHandle: string;
  providerSwitch: string;
}

export interface TerminalTheme {
  tokens?: Partial<TerminalThemeTokens>;
  classNames?: Partial<TerminalClassNames>;
  /** Force a color scheme. Default follows the token values as authored. */
  colorScheme?: "light" | "dark";
}

/** Dark-first defaults. Every value is a CSS custom property target. */
export const DEFAULT_TOKENS: TerminalThemeTokens = {
  bg: "#0b0f17",
  surface: "#141a24",
  fg: "#e6edf3",
  fgMuted: "#8b98a5",
  accent: "#4f8cff",
  accentFg: "#ffffff",
  border: "#222b38",
  danger: "#f0616d",
  fontMono:
    "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
  radius: "8px",
};

/** Maps a token key to its CSS custom property name. */
export function tokenVar(key: keyof TerminalThemeTokens): string {
  return `--rt-${key.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase())}`;
}

/** Builds the inline `style` object of CSS custom properties for the root. */
export function tokensToStyle(
  tokens?: Partial<TerminalThemeTokens>,
): Record<string, string> {
  const merged = { ...DEFAULT_TOKENS, ...tokens };
  const style: Record<string, string> = {};
  (Object.keys(merged) as (keyof TerminalThemeTokens)[]).forEach((key) => {
    style[tokenVar(key)] = merged[key];
  });
  return style;
}
